import type React from "react";

import { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  IconButton,
  Paper,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Add as AddIcon,
  ViewList as ViewListIcon,
  Notifications as NotificationsIcon,
} from "@mui/icons-material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import Badge from "@mui/material/Badge";
import { useGetUnreadCountQuery } from "../Slices/notificationsApiSlice";

import { settingsMenuItems } from "./constant/menu-items";
import { useDispatch, useSelector } from "react-redux";
import LoginIcon from "@mui/icons-material/Login";
import PriceCheckIcon from "@mui/icons-material/PriceCheck";
import { logout } from "../Slices/authSlice";

import { useNavigate } from "react-router-dom";
import { signOut } from "aws-amplify/auth";
// import RootState from your Redux store definition
import { RootState } from "store"; // Adjust the path as needed to your store file
import { useImageUrl } from "hooks/useImageUrl";
import { useTranslation } from "react-i18next";
import LanguageToggle from "components/shared/components/LanguageToggle";

const NewNavbar = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);

  // Fetch unread notification count (only when user is logged in)
  const { data: unreadData } = useGetUnreadCountQuery(undefined, {
    skip: !user,
    pollingInterval: 60000, // Poll every minute for new notifications
  });

  const { data: profilePictureUrl, isLoading: isLoadingImageUrl } = useImageUrl(
    user?.displayImg,
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    try {
      signOut();
      //clrar sessionStorage
      sessionStorage.clear();
      localStorage.clear();
      dispatch(logout());
      navigate("/home");
    } catch (err) {
      console.log({ err });
    }
    handleClose();
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  // Get user initial
  const getUserInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  return (
    <AppBar
      position="sticky"
      elevation={2}
      sx={{ bgcolor: "background.paper", color: "text.primary", boxShadow: 3 }}
    >
      <Toolbar sx={{ justifyContent: "space-between" }}>
        {/* Left side - Logo/Brand */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 3,
            cursor: "pointer",
          }}
          onClick={() => handleNavigation("/home")}
        >
          <Paper
            elevation={2}
            sx={{
              p: 1,
              bgcolor: "primary.main",
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AccountBalanceIcon sx={{ color: "white", fontSize: 28 }} />
          </Paper>
          <Typography
            variant="h6"
            noWrap
            sx={{
              mr: 2,
              display: { xs: "none", md: "flex" },
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: "1.35rem",
              letterSpacing: "0.01em",
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
            }}
            onClick={() => navigate("/home")}
          >
            Sanduuq
          </Typography>
        </Box>

        {/* Center - Main Navigation */}
        {user && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ViewListIcon />}
              onClick={() => handleNavigation("/view-group")}
              sx={{ textTransform: "none" }}
            >
              {/* for desktop, show the text */}
              <Typography sx={{ display: { xs: "none", md: "block" } }}>
                {t("nav.viewGroups")}
              </Typography>
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleNavigation("/create-group")}
              sx={{ textTransform: "none" }}
            >
              <Typography
                sx={{
                  display: { xs: "none", md: "block" },
                  fontSize: { xs: "0.8rem", md: "1rem" },
                }}
              >
                {t("nav.createGroup")}
              </Typography>
            </Button>
          </Box>
        )}

        {/* Right side - User Menu */}
        {user && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <LanguageToggle />
            {/* Notification Icon */}
            <IconButton
              onClick={() => handleNavigation("/notifications")}
              size="small"
              sx={{
                color: "text.secondary",
                "&:hover": {
                  color: "primary.main",
                  bgcolor: "action.hover",
                },
              }}
              aria-label="notifications"
            >
              <Badge
                badgeContent={unreadData?.count || 0}
                color="error"
                max={9}
                sx={{
                  "& .MuiBadge-badge": {
                    fontSize: "0.7rem",
                    height: 18,
                    minWidth: 18,
                    fontWeight: 600,
                  },
                }}
              >
                <NotificationsIcon />
              </Badge>
            </IconButton>

            <IconButton
              onClick={handleClick}
              size="small"
              sx={{ ml: 1 }}
              aria-label="Open account menu"
              aria-controls={open ? "account-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
            >
              <Avatar
                alt={user.firstName}
                src={profilePictureUrl || ""}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: "primary.main",
                  fontSize: "1rem",
                  fontWeight: "bold",
                }}
              >
                {!user.displayImg && getUserInitials(user.firstName)}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              id="account-menu"
              open={open}
              onClose={handleClose}
              onClick={handleClose}
              PaperProps={{
                elevation: 3,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                  mt: 1.5,
                  minWidth: 220,
                  "& .MuiAvatar-root": {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  "&:before": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: "background.paper",
                    transform: "translateY(-50%) rotate(45deg)",
                    zIndex: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              {/* User Info Header */}
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="subtitle2" noWrap>
                  {user.firstName}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {user.email}
                </Typography>
              </Box>
              {/* Settings Main Link */}
              <MenuItem onClick={() => handleNavigation("/settings")}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t("nav.settings")}</ListItemText>
              </MenuItem>
              <Divider />
              {/* Settings Subsections */}
              <Box sx={{ py: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ px: 2, color: "text.secondary", fontWeight: "bold" }}
                >
                  {t("nav.quickAccess")}
                </Typography>
                {settingsMenuItems.map((item) => (
                  <MenuItem
                    key={item.label}
                    onClick={() => handleNavigation(item.path)}
                    sx={{ pl: 3 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText>
                      <Typography variant="body2">{item.label}</Typography>
                    </ListItemText>
                  </MenuItem>
                ))}
              </Box>
              <Divider />
              {/* Logout */}
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t("nav.logout")}</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        )}
        {!user && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 2 },
            }}
          >
            <LanguageToggle />
            {/* At phone widths the two labels were wrapping into tall two-line
                buttons. The icons are decorative, so they yield first. */}
            <Button
              variant="outlined"
              startIcon={<LoginIcon />}
              onClick={() => handleNavigation("/login")}
              sx={{
                textTransform: "none",
                whiteSpace: "nowrap",
                flexShrink: 0,
                px: { xs: 1.25, sm: 2 },
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                "& .MuiButton-startIcon": {
                  display: { xs: "none", sm: "flex" },
                },
              }}
            >
              {t("nav.signIn")}
            </Button>
            <Button
              variant="contained"
              startIcon={<PriceCheckIcon />}
              onClick={() => handleNavigation("/register")}
              sx={{
                textTransform: "none",
                whiteSpace: "nowrap",
                flexShrink: 0,
                px: { xs: 1.25, sm: 2 },
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                "& .MuiButton-startIcon": {
                  display: { xs: "none", sm: "flex" },
                },
              }}
            >
              {t("nav.getStarted")}
            </Button>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default NewNavbar;
